// src/pages/HomePage.tsx — SCE Issues
// Accueil V2 (Lot 9, maquette validée le 25/09/2026).
//
// ─── Ce qui a changé ───
//
// L'accueil était le même pour tous : quatre chiffres d'ambiance (total,
// ouverts, critiques, clos du mois) qui ne menaient nulle part, puis « Mes
// déclarations » et « Derniers problèmes » en tuiles d'une centaine de pixels,
// dans une colonne de 760 px même sur un grand écran.
//
// Il dépend désormais du rôle affiché :
//  - coordinateur (et administrateur, sur tous les métiers) : ce qui l'attend
//    — à qualifier, critiques, sans activité depuis 7 jours, en cours — et
//    l'activité récente de ses métiers ;
//  - opérateur, fournisseur : déclarer, ce qui a bougé sur ses déclarations,
//    et les derniers problèmes (le sien y est peut-être déjà).
// Au bureau (≥ 1100 px), trois colonnes à hauteur d'écran sur 1 680 px ; au
// téléphone, une colonne et des listes courtes. Rangées au lieu de tuiles.
//
// Les problèmes viennent du même `useIssues()` qu'avant ; l'activité, les
// dates d'événements et la dernière visite de `useAccueil()`.

import { useMemo, useState } from 'react'
import { AppShell } from '@/components/layout/AppShell'
import { Header } from '@/components/layout/Header'
import { MenuCompte } from '@/components/layout/MenuCompte'
import { BoutonRafraichirProblemes } from '@/components/ui/BoutonRafraichir'
import { AccueilCoordinateur } from '@/components/accueil/AccueilCoordinateur'
import { AccueilOperateur } from '@/components/accueil/AccueilOperateur'
import { AccueilBE } from '@/components/accueil/AccueilBE'
import { metiersDuBE } from '@/config/be.config'
import { RechercheEntete } from '@/components/accueil/RechercheGlobale'
import { ListeProblemesSheet, type ListeProblemes } from '@/components/dashboard/ListeProblemesSheet'
import { useIssues } from '@/hooks/useIssues'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useAccueil } from '@/hooks/useAccueil'
import { useIdSharePoint } from '@/hooks/useIdSharePoint'
import { useElementsSuivis } from '@/hooks/useElementsSuivis'
import { useMaintenant } from '@/hooks/useMaintenant'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { estAdmin } from '@/config/admin.config'
import { getMetiersCoordonnes } from '@/config/coordinateurs.config'
import { rangeeMetiersEtSujets, sujetsDuResponsable } from '@/config/sujets.config'
import { estDeclarePar, identitesDeclarant } from '@/utils/identiteDeclarant'
import { salutation, trierMesDeclarations } from '@/utils/accueil'
import { problemesSuivis } from '@/utils/abonnements'
import type { CibleEtape, RoleRecap } from '@/utils/retourAbsence'
import { useRetourAbsence } from '@/hooks/useRetourAbsence'
import { RecapAbsence } from '@/components/accueil/RecapAbsence'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { useLocation, useNavigate } from 'react-router-dom'

/** En deçà, une colonne : trois colonnes demandent de la place. */
const ACCUEIL_LARGE = '(min-width: 1100px)'
/** Au-delà, une fiche ouverte depuis le récap s'affiche à côté de lui (28/10). */
const RECAP_A_COTE = '(min-width: 1300px)'

export function HomePage(): JSX.Element {
  const { issues, loading, error } = useIssues()
  const { user, isCoordinateur, isFournisseur, enVueRole, nomReel } = useCurrentUser()
  const large = useMediaQuery(ACCUEIL_LARGE)
  const maintenant = useMaintenant()
  const [liste, setListe] = useState<ListeProblemes | null>(null)

  // Identité AFFICHÉE (bascule de rôle comprise) : l'administrateur en vue
  // Opérateur voit l'accueil d'un opérateur.
  const admin = !!user && estAdmin(user.upn)
  const pilote = isCoordinateur || admin
  // Et les sujets dont il est responsable (23/10 : Thibaut, Goulotte).
  const metiers = useMemo(
    () => (isCoordinateur && user ? rangeeMetiersEtSujets(getMetiersCoordonnes(user.upn), sujetsDuResponsable(user.upn)) : []),
    [isCoordinateur, user],
  )
  const accueil = useAccueil(pilote)
  // Problèmes suivis (Lot 12) : reconnus par l'identifiant SharePoint de la
  // personne affichée, résolu une fois par session.
  const monId = useIdSharePoint(user?.upn)
  const upn = user?.upn ?? ''
  // Et les projets (Lot 12 bis) et équipements (Lot 12 ter) suivis : leurs
  // problèmes actifs aussi.
  const { elements: elementsSuivis } = useElementsSuivis(user?.upn)

  // Sur l'identifiant et non le nom affiché : Azure AD ne garantit l'unicité
  // d'aucun displayName (règle des deux identités : utils/identiteDeclarant).
  const mesDeclarations = useMemo(() => {
    if (!user) return []
    const identites = identitesDeclarant(user)
    return trierMesDeclarations(issues.filter((i) => estDeclarePar(i.declarant, identites)))
  }, [issues, user])

  const prenom = user?.prenom ?? ''
  const moi = user?.nom ?? ''

  // ─── Retour d'absence (28/10) : le récap en cartes, selon le rôle affiché ───
  const roleRecap: RoleRecap | null = !user || isFournisseur ? null : admin ? 'admin' : pilote ? 'pilote' : 'operateur'
  const identites = useMemo(() => (user ? identitesDeclarant(user) : []), [user])
  const mesIds = useMemo(() => new Set(mesDeclarations.map((i) => i.id)), [mesDeclarations])
  const suivisRecap = useMemo(() => problemesSuivis(issues, monId, upn, undefined, elementsSuivis), [issues, monId, upn, elementsSuivis])
  const retour = useRetourAbsence({
    upn,
    // La personne AFFICHÉE ; son nom tel que les FNC-T l'écrivent (CarteActionsFnct).
    nom: user ? (enVueRole ? user.nom : nomReel || user.nom) : '',
    prenom,
    identites,
    role: roleRecap,
    perimetre: metiers,
    issues,
    mesIds,
    suivis: suivisRecap,
    enVueRole,
    maintenant,
  })
  const ouvrirFiche = useOuvrirFiche()
  const navigate = useNavigate()
  const location = useLocation()
  // Assez de place pour le récap (460 px) ET la fiche (720 px) côte à côte.
  const recapACote = useMediaQuery(RECAP_A_COTE)
  function allerVers(cible: CibleEtape, carte: number): void {
    if (cible.genre === 'accueil') return retour.fermer()
    const aCote = cible.genre === 'fiche' && recapACote
    retour.partirVers(carte, aCote)
    if (cible.genre === 'page') return navigate(cible.chemin)
    // Une fiche déjà ouverte à côté du récap : la nouvelle la REMPLACE, la croix
    // ramène toujours au récap (sinon elle reculerait sur la précédente).
    if (aCote && retour.ficheOuverte) return navigate(cible.chemin, { replace: true, state: { fond: location, profondeur: 0 } })
    ouvrirFiche(cible.chemin)
  }

  const header = (
    <Header
      level={1}
      title="SCE Issues"
      width="large"
      marque
      centerAction={<RechercheEntete issues={issues} />}
      rightAction={
        <span className="flex items-center">
          <BoutonRafraichirProblemes />
          <MenuCompte />
        </span>
      }
    />
  )

  return (
    <AppShell header={header} width="large">
      {/* Un seul bandeau pour toute la page : toutes les sections viennent du
          même appel useIssues(). */}
      {error && (
        <div
          role="alert"
          style={{
            margin: large ? '16px 40px 0' : '12px 16px 0',
            padding: 12,
            borderRadius: 8,
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-error)',
            color: 'var(--color-error)',
            fontSize: 'var(--font-size-copy2)',
          }}
        >
          {error}
        </div>
      )}

      {/* Une colonne sous 1 100 px : plafonnée comme les autres pages, sinon
          elle s'étirerait sur toute une tablette en paysage. */}
      <div style={large ? undefined : { maxWidth: 'var(--width-content)', marginInline: 'auto' }}>
      {/* Une personne du BE (26/10, provisoire en attendant la maquette du 20/10) :
          les problèmes envoyés à son BE et ceux de son métier en cours. */}
      {!pilote && user && metiersDuBE(user.upn).length > 0 ? (
        <AccueilBE issues={issues} loading={loading} upn={user.upn} bonjour={salutation(prenom, maintenant, accueil.visitePrecedente, accueil.retour?.retour ?? null)} />
      ) : pilote ? (
        <AccueilCoordinateur
          issues={issues}
          loading={loading}
          accueil={accueil}
          metiers={metiers}
          prenom={prenom}
          moi={moi}
          monId={monId}
          upn={upn}
          elementsSuivis={elementsSuivis}
          maintenant={maintenant}
          large={large}
          ouvrirListe={setListe}
        />
      ) : (
        <AccueilOperateur
          issues={issues}
          loading={loading}
          accueil={accueil}
          prenom={prenom}
          moi={moi}
          mesDeclarations={mesDeclarations}
          monId={monId}
          upn={upn}
          elementsSuivis={elementsSuivis}
          maintenant={maintenant}
          large={large}
          ouvrirListe={setListe}
        />
      )}
      </div>

      {liste && <ListeProblemesSheet liste={liste} onClose={() => setListe(null)} />}
      {retour.ouvert && retour.recap && (
        <RecapAbsence recap={retour.recap} prenom={prenom} carteInitiale={retour.carte} aCote={retour.aCote} onFermer={retour.fermer} onMarquer={retour.marquer} onAller={allerVers} />
      )}
    </AppShell>
  )
}
